<template>
  <div v-if="allSections.length">
    <!-- options -->
    <div>
      <BaseButton
        :text="`Back to 'Your application'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="
          $router.push({ name: ROUTE_NAME.TENANTS_LIABILITY.B2B.APPLICATION })
        "
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Payment
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>
    </div>

    <!-- the form -->
    <ValidationObserver ref="observerFormPaymentDetails" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <BaseLabel text="Payment method" class="mt-10 mb-2" />
        <BaseToggleButton
          v-model="paymentMethodLocal"
          :toggleButtonText="{
            yes: 'Direct Debit',
            yesDataUiid: 'PaymentMethod_DirectDebit',
            no: $vuetify.breakpoint.lgAndUp
              ? 'Credit / Debit Card'
              : 'Credit Card',
            noDataUiid: 'PaymentMethod_CreditCardDirectDebit',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '240' : ''"
        />

        <!-- payment frequency -->
        <template v-if="isDirectDebit">
          <BaseLabel text="Payment frequency" class="mt-10 mb-2" />
          <BaseSelect
            v-model="paymentFrequency.Value"
            dataUiid="PaymentFrequency"
            :items="
              setItemDataUiids('PaymentFrequency', generatePaymentFrequency)
            "
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Payment frequency',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Payment frequency"
          />
        </template>

        <LayoutApplicationSummaryCard
          class="mt-12 mb-13"
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
          @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
          @shareQuote="shareQuote"
          @printQuote="printQuote"
          :productGroup="productGroup"
          :cardTitle="
            $vuetify.breakpoint.lgAndUp
              ? 'Your Tenant’s Liability Insurance quote - ' + getMonthCoverText
              : 'Your quote'
          "
          annualTitle="One off payment"
          :showPayByCreditDebitCardMessage="false"
          :useRetrievedPaymentInfo="$route.params.type === 'resumeQuote'"
        />

        <!-- direct debit content -->
        <template v-if="isDirectDebit">
          <BaseAlert
            color="var(--blue-100)"
            class="pa-4"
            v-if="hasCreditAgreement"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important!</span>&nbsp;By
                selecting the monthly payment option you have chosen to enter
                into a credit agreement. Failure to keep up the payments is a
                breach of this agreement and may result in the cancellation of
                your insurance policy. Please tick the box below to confirm that
                the repayments are affordable and you are happy to enter into a
                credit agreement. If you feel the payments may not be
                affordable, please consider other payment options.
              </div>
            </div>
          </BaseAlert>

          <BaseCheckBox
            v-if="hasCreditAgreement"
            className="mt-3 font-weight-bold primary--text"
            label="I confirm that I have read, understood and agreed to the above statement."
            hideDetails="auto"
            :checked="repaymentConfirmation.Value"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Repayment confirmation',
              },
            }"
            @change="(val) => (repaymentConfirmation.Value = val)"
            aria-label="Repayment confirmation"
          />

          <h2 class="font-20 font-weight-bold primary--text mt-10 mb-3">
            Payment details
          </h2>

          <BaseAlert
            color="var(--blue-100)"
            icon="mdi-lock"
            alertText="Your payment is secure."
            alertTextClass="font-18 font-weight-bold primary--text"
            alertBorder
            alertBorderColor="var(--blue-500)"
            alertBorderPosition="left"
            prominent
            prominentColor="var(--blue-500)"
            prominentIconColor="white"
            prominentIconSize="19"
            rounded="0"
            :max-width="$vuetify.breakpoint.lgAndUp ? '571px' : '100%'"
          />

          <BaseLabel
            text="Account holder name"
            hintText="How does your name appear on your bank account?"
            class="mt-10 mb-2"
          />
          <BaseInput
            :vid="bankAccountName.InterfaceKey"
            v-model="bankAccountName.Value"
            dataUiid="AccountHolderName"
            className="mb-9"
            hideDetails="auto"
            :rules="bankAccountNameRule"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '571px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Account holder name"
          />

          <div class="error-message-fit-input">
            <div class="d-block d-lg-inline-block">
              <BaseLabel text="Sort code" class="mb-2" />
              <BaseInput
                :vid="bankSortCode.InterfaceKey"
                v-model="bankSortCode.Value"
                dataUiid="SortCode"
                className="mb-9"
                hideDetails="auto"
                mask="##-##-##"
                :rules="bankSortCodeRule"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '219px' : '100%',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                aria-label="Sort code"
              />
            </div>
            <div class="d-block d-lg-inline-block ml-lg-6 acc_no__wrapper">
              <BaseLabel text="Account number" class="mb-2" />
              <BaseInput
                :vid="bankAccountNo.InterfaceKey"
                v-model="bankAccountNo.Value"
                dataUiid="AccountNumber"
                className="mb-9"
                hideDetails="auto"
                :rules="bankAccountNoRule"
                :style="{
                  'max-width': $vuetify.breakpoint.lgAndUp ? '328px' : '100%',
                  'min-width': $vuetify.breakpoint.lgAndUp ? '328px' : '',
                }"
                :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                aria-label="Account number"
              />
            </div>
          </div>

          <BaseLabel :text="generateLabelPreferedDirectDebit" class="mb-2" />
          <BaseSelect
            :vid="directDebitDate.InterfaceKey"
            v-model="directDebitDate.Value"
            dataUiid="DirectDebitDate"
            :items="
              setItemDataUiids(
                'DirectDebitDate',
                DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL
              )
            "
            :isEnableSortOrderListItems="false"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'Preferred DD date',
              },
            }"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '571px' : '100%',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            aria-label="Preferred DD date"
          />

          <v-alert
            color="var(--orange-100)"
            class="mt-4 mb-0 rounded-lg"
            v-if="directDebitDate.Value"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--orange-500)">mdi-alert</v-icon>
              </div>
              <div>
                <span class="font-weight-bold">Important!</span> It can take up
                to ten working days to set up a Direct Debit which means your
                first payment may not be taken on your preferred date. The date
                of your first payment will be shown in your Welcome Letter. The
                rest of your payments will be taken on the date you choose from
                the options provided.
              </div>
            </div>
          </v-alert>

          <div
            class="d-flex flex-column flex-lg-row align-lg-center mt-10 mb-3"
          >
            <h3 class="font-20 font-weight-bold primary--text">Stay covered</h3>
            <a
              href="#"
              class="d-lg-none font-16 font-weight-bold psdarkgreen--text mt-1"
              @click.prevent="showAutoRenewalInfoMobileDialog"
              >Find out more</a
            >
          </div>

          <BaseRadio
            :className="$vuetify.breakpoint.lgAndUp ? 'pr-6' : ''"
            v-model="autoRenewalConsent.Value"
            data-uiid="AutoRenewalConsent"
            pslStyle="card"
            :cardConfigs="{
              autoRenew: AUTO_RENEW_NONE_CHEAPEST_PRICE,
              doNotAutoRenew: DO_NOT_AUTO_RENEW_NONE_OFFER_TENANT,
            }"
          />
        </template>

        <!-- credit/debit card content -->
        <template v-else>
          <BaseAlert
            color="var(--blue-100)"
            class="credit-debit-alert pa-4 pr-6 ma-0"
          >
            <div class="d-flex flex-no-wrap">
              <div class="mr-3">
                <v-icon color="var(--blue-500)">mdi-information</v-icon>
              </div>
              <div>
                <p>
                  <span class="font-weight-bold">Important!</span>&nbsp;As
                  you’ve chosen to pay by annual credit/debit card, you’ve been
                  opted out of our auto-renewal service. If you would prefer to
                  opt into our auto-renewal service, please change your payment
                  method to Direct Debit above.
                </p>
                <p class="mb-0">
                  Paymentshield will contact you with a renewal quote, which
                  includes the price and cover, 30 days before your renewal is
                  due. You will need to contact Paymentshield to renew the
                  policy or you will need to arrange cover elsewhere.
                </p>
              </div>
            </div>
          </BaseAlert>
        </template>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3 primary--text"
          type="submit"
          icon="mdi mdi-lock"
          :loading="isApplying"
          text="SUBMIT"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
        />
      </form>
    </ValidationObserver>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- missing bank details dialog -->
    <BaseDialog
      ref="dialogDirectDebitMissingBankDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
    >
      <template v-slot:content>
        <DialogContentMissingBankDetails
          @missingBankDetails="handleMissingBankDetails"
          @cancelMissingBankDetails="cancelMissingBankDetails"
          :sortCodeLength="bankSortCode.Value.length"
          :accountNumberLength="bankAccountNo.Value.length"
        />
      </template>
    </BaseDialog>

    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          insurancePremiumTitle="Tenants Liability premium (ex IPT)"
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <!-- share dialog -->
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getTenantsLiabilityCover"
          :quoteId="data"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <!-- print dialog -->
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>

    <!-- mobile auto renewal info dialog -->
    <BaseDialog
      ref="autoRenewalInfoMobile"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-2"
      contentClass="mb-0"
    >
      <template v-slot:content>
        <DialogContentAutoRenewalInfoMobile
          :cardConfigs="{
            autoRenew: AUTO_RENEW_NONE_CHEAPEST_PRICE,
            doNotAutoRenew: DO_NOT_AUTO_RENEW_NONE_OFFER_TENANT,
          }"
        ></DialogContentAutoRenewalInfoMobile>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { ValidationObserver } from "vee-validate";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { setItemDataUiids } from "@/constants/component.constants.js";
import {
  PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF,
  DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
  AUTO_RENEW_NONE_CHEAPEST_PRICE,
  DO_NOT_AUTO_RENEW_NONE_OFFER_TENANT,
} from "@/constants/payment.constants.js";
import { scrollTop, getErrorMessage, convertToBool } from "@/common/utils";

// LayoutApplicationSummaryCard dependencies
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { ANNUAL, MONTHLY } from "@/constants/common.constants";
import { ROUTE_NAME } from "@/constants/route.constants";
import { getProductGroup } from "@/common/question-set";
import { TERM_OF_INSURANCE_VALUE } from "@/constants/answer.constants";
import {
  isBankDetailsBlank,
  getBankAccountNameRule,
  getBankSortCodeRule,
  getBankAccountNoRule,
} from "@/common/bankDetailsValidation";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseCheckBox from "@/components/BaseComponents/BaseCheckBox";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import BaseRadio from "@/components/BaseComponents/BaseRadio";

// LayoutApplicationSummaryCard components
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard";
import DialogContentMissingBankDetails from "@/views/StageApplication/DialogContentMissingBankDetails";
import DialogContentPremiumBreakdown from "@/components/QuoteResult/DialogContentPremiumBreakdown";
import DialogContentShareQuotes from "@/components/QuoteResult/DialogContentShareQuotes";
import DialogContentPrintQuotes from "@/components/QuoteResult/DialogContentPrintQuotes";
import DialogContentAutoRenewalInfoMobile from "@/views/StageApplication/DialogContentAutoRenewalInfoMobile";

export default {
  name: "TenantsLiabilityPaymentPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseToggleButton,
    BaseSelect,
    BaseAlert,
    BaseCheckBox,
    BaseInput,
    BaseDialog,
    BaseRadio,
    LayoutApplicationSummaryCard,
    DialogContentMissingBankDetails,
    DialogContentPremiumBreakdown,
    DialogContentShareQuotes,
    DialogContentPrintQuotes,
    DialogContentAutoRenewalInfoMobile,
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      setItemDataUiids,
      PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF,
      DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
      AUTO_RENEW_NONE_CHEAPEST_PRICE,
      DO_NOT_AUTO_RENEW_NONE_OFFER_TENANT,
      ANNUAL,
      MONTHLY,
      ROUTE_NAME,
      paymentMethod: null,
      paymentMethodLocal: true,
      paymentFrequency: null,
      repaymentConfirmation: null,
      bankAccountName: null,
      bankSortCode: null,
      bankAccountNo: null,
      directDebitDate: null,
      autoRenewalConsent: null,
      quoteDocuments: [],
      documentList: [],
      overlay: false,
      isApplying: false,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, [
      "allSections",
      "selectedQuote",
      "getMonthCoverText",
    ]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getTenantsLiabilityCover",
      "getQuoteRequestId",
      "callChannel",
      "httpConfig",
      "insurerQuotesModel",
      "qsId",
      "branchNumber",
      "termOfInsurance",
    ]),
    ...mapState(APPLICATION_MODULE, ["questionMap"]),
    generatePaymentFrequency() {
      let frequencyItems = this.PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF;
      if (this.termOfInsurance === TERM_OF_INSURANCE_VALUE.SixMonths) {
        frequencyItems = this.PAYMENT_FREQUENCY_ITEMS_ANNUAL_ONE_OFF.filter(
          (item) => {
            return item.value === "Annual";
          }
        );
      }
      return frequencyItems;
    },
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isDirectDebit() {
      return this.paymentMethodLocal;
    },
    hasCreditAgreement() {
      return this.paymentFrequency.Value === MONTHLY;
    },
    generateLabelPreferedDirectDebit() {
      return this.paymentFrequency.Value === MONTHLY
        ? "What date do you want your monthly payment to be made?"
        : "What date do you want your payment to be made?";
    },
    paymentFrequencyForDocs() {
      // Prevent returning an empty string for Payment Frequency if the payment method is card.
      return this.paymentMethod.Value === "CreditCard"
        ? ANNUAL
        : this.paymentFrequency.Value;
    },
    isDirectDebitDetailsValid() {
      return !!this.bankSortCode.Value && !!this.bankAccountNo.Value;
    },
    isDirectDebitDetailsBlank() {
      return isBankDetailsBlank(
        this.bankSortCode.Value,
        this.bankAccountNo.Value,
        this.bankAccountName.Value
      );
    },
    bankAccountNameRule() {
      return getBankAccountNameRule(this.isDirectDebitDetailsBlank);
    },
    bankSortCodeRule() {
      return getBankSortCodeRule(this.isDirectDebitDetailsBlank);
    },
    bankAccountNoRule() {
      return getBankAccountNoRule(this.isDirectDebitDetailsBlank);
    },
    premium() {
      const breakdown = this.buildPremiumBreakdownModel(
        this.selectedQuote,
        "DirectDebit",
        this.paymentFrequency.Value
      );
      return breakdown.premium;
    },
  },
  watch: {
    paymentMethodLocal(val) {
      if (val) {
        this.paymentMethod.Value = "DirectDebit";
      } else {
        this.paymentMethod.Value = "CreditCard";
      }
    },
  },
  created() {
    if (this.allSections.length) {
      this.paymentMethod = this.questionMap.PaymentMethod;
      if (!this.paymentMethod.Value) this.paymentMethod.Value = "DirectDebit";
      if (this.paymentMethod.Value && this.paymentMethod.Value === "CreditCard")
        this.paymentMethodLocal = false;

      this.paymentFrequency = this.questionMap.PaymentFrequency;
      this.repaymentConfirmation = this.questionMap.RepaymentConfirmation;
      this.bankAccountName = this.questionMap.BankAccountName;
      this.bankSortCode = this.questionMap.BankSortCode;
      this.bankAccountNo = this.questionMap.BankAccountNo;
      this.directDebitDate = this.questionMap.DirectDebitDate;
      this.autoRenewalConsent = this.questionMap.AutoRenewalConsent;

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.repaymentConfirmation.Value = convertToBool(
        this.repaymentConfirmation.Value
      );
      this.autoRenewalConsent.Value = convertToBool(
        this.autoRenewalConsent.Value
      );
    }
  },
  mounted() {
    scrollTop();
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPaymentDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "getDataToPrintQuote",
      "saveQuoteResults",
      "getDocuments",
      "setCurrentRefObserverForm",
    ]),
    ...mapActions(APPLICATION_MODULE, [
      "setCardPaymentStatus",
      "handleApplicationSubmission",
      "setSessionStorageItems",
      "proceedToCardPayment",
      "setPaymentErrorMessage",
      "clearPaymentErrorMessage",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
        return;
      }

      if (this.isDirectDebit && !this.isDirectDebitDetailsValid) {
        this.$refs.dialogDirectDebitMissingBankDetails.open(
          "Missing bank details"
        );
      } else {
        this.submitForm();
      }
    },
    handleMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
      this.submitForm();
    },
    cancelMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
    },
    async shareQuote(quoteId) {
      try {
        this.overlay = true;

        await this.saveQuoteResults();

        this.$refs.shareDialog.open("Share via email", quoteId);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async printQuote(quote) {
      try {
        this.overlay = true;
        const docs = await this.saveQuotesAndGetDocumentsAsync();
        const paymentFrequency = this.paymentFrequencyForDocs;
        var data = await this.getDataToPrintQuote({
          docs,
          quote,
          paymentFrequency,
        });
        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    submitForm() {
      this.setCardPaymentStatus("");
      if (this.paymentMethod.Value === "DirectDebit") {
        this.submitDirectDebitApplication();
      } else {
        this.submitCardPaymentApplication();
      }
    },
    submitDirectDebitApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.isApplying = false;
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.$router.push({
            name: ROUTE_NAME.TENANTS_LIABILITY.B2B.COMPLETE,
          });
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
    submitCardPaymentApplication() {
      this.isApplying = true;

      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.clearPaymentErrorMessage();
          this.proceedToCardPayment();
        })
        .catch((error) => {
          this.isApplying = false;
          this.setPaymentErrorMessage(getErrorMessage(error));
          console.log("Apply error:", error);
        });
    },
    showAutoRenewalInfoMobileDialog() {
      this.$refs.autoRenewalInfoMobile.open("Stay covered");
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-text-field__details) {
  margin-bottom: 0 !important;
}
.credit-debit-alert {
  letter-spacing: 0.35px;
  line-height: 19.5px;
}
@media screen and (min-width: $grid-breakpoints-lg) {
  :deep(.radioButtonCard) {
    .v-card {
      min-height: 475px !important;
    }
  }
}
</style>
